import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth as authApi, setToken } from '../services/api';

interface AuthState {
  user: { username: string; role: string; permissions: string[]; display_name?: string } | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (page: string) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({} as AuthState);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthState['user']>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const res = await authApi.me();
      setUser({
        username: res.data.username,
        role: res.data.role || 'operations',
        permissions: res.data.permissions || [],
        display_name: res.data.display_name,
      });
    } catch {
      setUser(null);
      setToken(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('tg_token');
    if (token) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const res = await authApi.login(username, password);
    setUser({
      username: res.data.username,
      role: res.data.role || 'operations',
      permissions: res.data.permissions || [],
      display_name: res.data.display_name,
    });
  };

  const logout = () => {
    authApi.logout().catch(() => {});
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  const hasPermission = (page: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions.includes(page);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      loading,
      login,
      logout,
      hasPermission,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
